import { useBrandContext } from "@/contexts/brandContext";
import { NetworkStatus } from "@apollo/client";
import { useQuery } from "@apollo/client/react";
import { DataGrid } from "@repo/components/src/shared/dataGrid";
import Search from "@repo/components/src/shared/search";
import { Box } from "@repo/components/src/ui/box";
import { Icon } from "@repo/components/src/ui/icon";
import { Text } from "@repo/components/src/ui/text";
import { VStack } from "@repo/components/src/ui/vstack";
import { ListCampaignsWithReportDocument } from "@repo/graphql/documents/campaign/query.generated";
import { MontuCampaignStatusChoices } from "@repo/graphql/gql/graphql";
import { FlagOff } from "lucide-react-native";
import React, { useEffect, useMemo, useState } from "react";
import { finishedCampaignColumns } from "./columns";

export type FinishedCampaignsStats = {
  total: number;
};

type Props = {
  onStatsChange?: (stats: FinishedCampaignsStats) => void;
  /** Overrides each row's trailing action cell — defaults to "Ver campaña". */
  renderAction?: Parameters<typeof finishedCampaignColumns>[0]["renderAction"];
};

const PAGE_SIZE = 30;

const EmptyState = (
  <VStack className="items-center gap-2 py-10">
    <Box className="rounded-full bg-background-100 p-3">
      <Icon as={FlagOff} size="xl" className="text-typography-400" />
    </Box>
    <Text size="sm" className="text-typography-400">
      No hay campañas finalizadas
    </Text>
  </VStack>
);

const FinishedCampaigns = React.memo(
  ({ onStatsChange, renderAction }: Props) => {
    const { brandId } = useBrandContext();
    const [search, setSearch] = useState("");
    const { data, networkStatus, fetchMore } = useQuery(
      ListCampaignsWithReportDocument,
      {
        variables: {
          brand_Id: brandId,
          status_In: [MontuCampaignStatusChoices.Finished],
          search: search || undefined,
          orderBy: "-id",
          first: PAGE_SIZE,
        },
        fetchPolicy: "cache-and-network",
        notifyOnNetworkStatusChange: true,
      },
    );

    const connection = data?.campaign.list;
    const columns = useMemo(
      () => finishedCampaignColumns({ brandId, renderAction }),
      [brandId, renderAction],
    );

    useEffect(() => {
      if (!data || !onStatsChange) return;
      onStatsChange({
        total: connection?.totalCount ?? connection?.edges.length ?? 0,
      });
    }, [data, connection, onStatsChange]);

    return (
      <VStack className="w-full gap-3">
        <Search
          size="sm"
          placeholder="Buscar campañas finalizadas..."
          onChange={setSearch}
          loading={networkStatus === NetworkStatus.setVariables}
        />
        {/* The grid scrolls inside a capped box; the frame's own bottom border
         * keeps the table visually closed even when the cutoff lands mid-row. */}
        <Box className="max-h-[60vh] w-full overflow-hidden rounded-2xl border-b border-outline-200">
          <DataGrid
            getId={(item) => item.node.id}
            data={connection?.edges}
            networkStatus={networkStatus}
            columns={columns}
            checkbox={false}
            size="md"
            pageInfo={connection?.pageInfo}
            fetchMore={fetchMore}
            emptyComponent={EmptyState}
          />
        </Box>
      </VStack>
    );
  },
);

FinishedCampaigns.displayName = "FinishedCampaigns";

export default FinishedCampaigns;
